import { createContext, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { isPublicRoute } from 'src/compass/shared/hooks/useCasAuthenticated';

import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { acquireAuthToken, IContextChildren, IAuthContext } from 'src/compass/site/util';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import { onErrorPermissionsHandler } from 'src/compass/shared/util/onErrorHandlers';
import infoUriFromEntity from 'src/compass/shared/util/infoUriFromEntity';
import { paths } from '../routes';

export const AuthContext = createContext<IAuthContext>({
  token: null,
  userPermissions: null,
  loadingPermission: true,
  account: null,
  impersonatingUserName: null,
});

const AuthContextProvider = ({ children }: IContextChildren) => {
  const { instance, inProgress } = useMsal();
  const [token, setToken] = useState<string | null | undefined>(null);
  const [account, setAccount] = useState<any>(null);
  const [loadingPermission, setLoadingPermission] = useState(true);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { data } = useRetrieve({
    url: infoUriFromEntity('Login/Permission'),
    enabled: !!token,
    onError: (error: AxiosError) => {
      setLoadingPermission(false);
      onErrorPermissionsHandler(error);
    },
    onSuccess: (response: any) => {
      setLoadingPermission(false);
      return response;
    },
  });

  const { authorities, impersonatingUserName } = data || {};

  useEffect(() => {
    if (data !== undefined) {
      if (!authorities || authorities?.length === 0) navigate(paths.page401);
    }
  }, [data, authorities, navigate]);

  useEffect(() => {
    if (!token && inProgress === InteractionStatus.None) {
      const accountInfo = instance.getActiveAccount();
      setAccount(accountInfo);

      if (isPublicRoute()) {
        setToken(undefined);
      } else {
        (async () => {
          if (accountInfo) {
            const accessToken: any = await acquireAuthToken(instance, accountInfo);
            setToken(accessToken);
          }
        })();
      }
    }
  }, [inProgress, instance, token, enqueueSnackbar]);

  const valueObj = useMemo(
    () => ({
      token: token ?? null,
      userPermissions: authorities || [],
      loadingPermission,
      impersonatingUserName,
      account,
    }),
    [token, impersonatingUserName, authorities, loadingPermission, account]
  );

  if ((loadingPermission || !token) && account
      && token!==undefined) {
    return <CasLoader />;
  }

  return <AuthContext.Provider value={valueObj}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
